import { useEffect, useState, useCallback } from 'react'
import { propExistsOnObject, renderColorPicker, renderSelector } from '../utils'
import {
    BAR_DEFAULT_VALUES,
    FONT_FAMILIES,
    FONT_WEIGHTS,
    LABEL_DEFAULT_VALUES,
    H_ALIGNMENT,
    LINE_DEFAULT_VALUES,
    PIE_DEFAULT_VALUES,
} from '../styleConsts'
import { cloneDeep, debounce } from 'lodash'
import { ScoopSlider, ScoopSwitch, ScoopText } from '../../../../../ui'
import styleTabStyles from '../StyleTab.module.scss'

const LABEL_POSITION_OPTIONS = {
    bar: [
        { label: 'Center', value: 'inside' },
        { label: 'Inside Base', value: 'insideLeft' },
        { label: 'Inside End', value: 'insideRight' },
        { label: 'Outside End', value: 'outside' },
    ],
    column: [
        { label: 'Center', value: 'inside' },
        { label: 'Inside Base', value: 'insideBottom' },
        { label: 'Inside End', value: 'insideTop' },
        { label: 'Outside End', value: 'outside' },
    ],
    line: [
        { label: 'Top', value: 'top' },
        { label: 'Bottom', value: 'bottom' },
        { label: 'Left', value: 'left' },
        { label: 'Right', value: 'right' },
    ],
    area: [
        { label: 'Top', value: 'top' },
        { label: 'Bottom', value: 'bottom' },
        { label: 'Left', value: 'left' },
        { label: 'Right', value: 'right' },
    ],
    pie: [
        { label: 'Inside', value: 'inside' },
        { label: 'Outside', value: 'outside' },
    ],
    donut: [
        { label: 'Inside', value: 'inside' },
        { label: 'Outside', value: 'outside' },
    ],
}

const LabelPositionConfig = ({ config, setConfig, chartPreferences }) => {
    const getChartType = (type) => {
        const typeMap = {
            area: 'line',
            column: 'bar',
            donut: 'pie',
        }
        return typeMap[type] || type
    }

    const handlePositionChange = (value) => {
        const chartType = getChartType(config.seriesType)
        const newConfig = cloneDeep(config)
        newConfig.styleOverrides[chartType].position = value
        setConfig(newConfig)
    }

    const getPropValue = (prop) => {
        const chartType = getChartType(config.seriesType)
        const defaultValues = {
            bar: BAR_DEFAULT_VALUES,
            line: LINE_DEFAULT_VALUES,
            pie: PIE_DEFAULT_VALUES,
        }

        if (propExistsOnObject(config.styleOverrides[chartType], prop)) {
            return config.styleOverrides[chartType][prop]
        }
        if (propExistsOnObject(chartPreferences[chartType], prop)) {
            return chartPreferences[chartType][prop]
        }
        return defaultValues[chartType]?.[prop] || 'inside'
    }

    const currentOptions = LABEL_POSITION_OPTIONS[config.seriesType] || LABEL_POSITION_OPTIONS.bar

    return (
        <>
            {renderSelector(
                'Label position',
                getPropValue('position'),
                handlePositionChange,
                currentOptions
            )}
        </>
    )
}

export const LabelConfig = ({ config, setConfig, chartPreferences }) => {
    const [fontSize, setFontSize] = useState(LABEL_DEFAULT_VALUES.fontSize)
    const [rotate, setRotate] = useState(LABEL_DEFAULT_VALUES.rotate)
    const [distance, setDistance] = useState(LABEL_DEFAULT_VALUES.distance)

    const debouncedFontSize = useCallback(
        debounce((prop, value) => {
            const newConfig = cloneDeep(config)
            newConfig.styleOverrides.label[prop] = value
            setConfig(newConfig)
        }, 200),
        [config]
    )

    useEffect(() => {
        setFontSize(getLabelPropValue('fontSize'))
        setRotate(getLabelPropValue('rotate'))
    }, [])

    const getLabelPropValue = (prop) => {
        if (propExistsOnObject(config.styleOverrides.label, prop))
            return config.styleOverrides.label[prop]
        if (propExistsOnObject(chartPreferences?.label, prop)) return chartPreferences.label[prop]
        return LABEL_DEFAULT_VALUES[prop]
    }

    const handleLabelChange = (prop, value) => {
        const newConfig = cloneDeep(config)
        newConfig.styleOverrides.label[prop] = value
        setConfig(newConfig)
    }

    const handleDebouncedLabelChange = (prop, value, setter) => {
        debouncedFontSize(prop, value)
        setter(value)
    }

    const show = getLabelPropValue('show')

    return (
        <div className={styleTabStyles.configContainer}>
            <div className={styleTabStyles.switchSection}>
                <ScoopText className={styleTabStyles.switchLabel}>Label</ScoopText>
                <ScoopSwitch checked={show} onChange={() => handleLabelChange('show', !show)} />
            </div>
            {renderColorPicker('Color', getLabelPropValue('color'), (v) =>
                handleLabelChange('color', v)
            )}
            {renderSelector(
                'Font family',
                getLabelPropValue('fontFamily'),
                (v) => handleLabelChange('fontFamily', v),
                FONT_FAMILIES
            )}
            <ScoopSlider
                label={`Font size: ${fontSize}px`}
                value={fontSize}
                onChange={(value) => handleDebouncedLabelChange('fontSize', value, setFontSize)}
                min={8}
                max={64}
                step={1}
            />
            {renderSelector(
                'Font weight',
                getLabelPropValue('fontWeight'),
                (v) => handleLabelChange('fontWeight', v),
                FONT_WEIGHTS
            )}
            <ScoopSlider
                label={`Rotate: ${rotate}deg`}
                value={rotate}
                onChange={(value) => handleDebouncedLabelChange('rotate', value, setRotate)}
                min={0}
                max={360}
                step={45}
            />
            <ScoopSlider
                label={`Gap: ${distance}`}
                value={distance}
                onChange={(value) => handleDebouncedLabelChange('distance', value, setDistance)}
                min={5}
                max={50}
                step={1}
            />
            <LabelPositionConfig
                config={config}
                setConfig={setConfig}
                chartPreferences={chartPreferences}
            />
            {renderSelector(
                'Horizontal align',
                getLabelPropValue('align'),
                (v) => handleLabelChange('align', v),
                H_ALIGNMENT
            )}
        </div>
    )
}
